import { SettingOutlined } from '@ant-design/icons'
import { Button, message, Skeleton, Tooltip } from 'antd'
import {
  aggregateNiiSavings,
  formatAed,
  formatAedFull,
  getTarRoiBySiteFromGroup,
  ROI_CATEGORY_COLORS,
  ROI_CATEGORY_LABELS,
  type RoiSavingsCategory,
} from 'features/libra/roi/niiRoiModel'
import type {
  LibraTicket,
  LibraTicketGroup,
} from 'features/libra/ticket/libraTicketsApi'
import { ELIGIBILITY_STATUS } from 'features/libra/types/eligibility'
import { PermissionsGate, useGeckoPermissions } from 'features/permissions'
import { useRolodexEntries } from 'features/rolodex'
import uniq from 'lodash/uniq'
import type { FC } from 'react'
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_LIBRA_EXECUTIVE,
  RolodexEntryType,
} from 'types'

import { RoiAssumptionsSettingsModal } from './RoiAssumptionsSettingsModal'

type ExecutiveDashboardProps = {
  tickets: ReadonlyArray<LibraTicket>
  isLoading?: boolean
  totalAssets?: number
  group: LibraTicketGroup
  selectedSiteId: string | undefined
  onRefreshGroup: () => void
  siteFilterSiteIds: ReadonlyArray<string>
}

const ROI_ORDER: RoiSavingsCategory[] = [
  'inspection',
  'hse',
  'production',
  'labor',
]

export const ExecutiveDashboard: FC<ExecutiveDashboardProps> = ({
  tickets,
  isLoading: parentIsLoading = false,
  totalAssets,
  group,
  selectedSiteId: _selectedSiteId,
  onRefreshGroup,
  siteFilterSiteIds,
}) => {
  const { hasPermissions } = useGeckoPermissions()
  const hasExecutiveRoiAccess = hasPermissions([
    PERMISSION_GECKO_INTERNAL_MEMBER,
    PERMISSION_LIBRA_EXECUTIVE,
  ])

  const isLoading = parentIsLoading
  const [assumptionsOpen, setAssumptionsOpen] = useState(false)

  const { entries: siteEntries, isLoading: siteEntriesLoading } =
    useRolodexEntries(
      useMemo(
        () => ({
          type: RolodexEntryType.SITE,
          ids: [...siteFilterSiteIds],
        }),
        [siteFilterSiteIds]
      )
    )

  const siteOptions = useMemo(
    () =>
      siteEntries
        .map((e) => ({
          value: e.id,
          label: (e.data.name as string) ?? e.id,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label)),
    [siteEntries]
  )

  const unitIdsWithTickets = useMemo(() => {
    return uniq(
      tickets
        .map((ticket) => ticket.tags.unit)
        .filter((unit): unit is string => unit !== undefined)
    )
  }, [tickets])

  const totalScreenedAssets = unitIdsWithTickets.length

  const assetsReviewed = useMemo(() => {
    if (isLoading) return 0
    const uniqueUnits = uniq(
      tickets
        .filter((ticket) => ticket.workflowComplete)
        .map((ticket) => ticket.tags.unit)
        .filter((unit): unit is string => unit !== undefined)
    )
    return uniqueUnits.length
  }, [tickets, isLoading])

  const completionDenominator = totalAssets ?? totalScreenedAssets

  const completionRate = useMemo(() => {
    if (completionDenominator === 0) return 0
    return Math.round((assetsReviewed / completionDenominator) * 100)
  }, [assetsReviewed, completionDenominator])

  const niiEligibleAssets = useMemo(() => {
    if (isLoading) return 0
    const uniqueEligibleUnits = uniq(
      tickets
        .filter(
          (ticket) =>
            ticket.workflowComplete &&
            ticket.fieldValues?.eligibility?.value ===
              ELIGIBILITY_STATUS.ELIGIBLE
        )
        .map((ticket) => ticket.tags.unit)
        .filter((unit): unit is string => unit !== undefined)
    )
    return uniqueEligibleUnits.length
  }, [tickets, isLoading])

  const eligibilityRate = useMemo(() => {
    if (assetsReviewed === 0) return 0
    return Math.round((niiEligibleAssets / assetsReviewed) * 100)
  }, [niiEligibleAssets, assetsReviewed])

  const { byCategory, barTooltips } = useMemo(
    () => aggregateNiiSavings(tickets, getTarRoiBySiteFromGroup(group)),
    [tickets, group]
  )

  if (isLoading) {
    return (
      <ChartsGrid $threeCol={hasExecutiveRoiAccess}>
        <ChartCard>
          <Skeleton active title={false} paragraph={{ rows: 3 }} />
        </ChartCard>
        <ChartCard>
          <Skeleton active title={false} paragraph={{ rows: 3 }} />
        </ChartCard>
        <PermissionsGate
          requires={[
            PERMISSION_GECKO_INTERNAL_MEMBER,
            PERMISSION_LIBRA_EXECUTIVE,
          ]}>
          <RoiColumn>
            <ChartCard>
              <Skeleton active title={false} paragraph={{ rows: 3 }} />
            </ChartCard>
          </RoiColumn>
        </PermissionsGate>
      </ChartsGrid>
    )
  }

  return (
    <ChartsGrid $threeCol={hasExecutiveRoiAccess}>
      <ChartCard>
        <CardHeader>NII Eligible Assets</CardHeader>
        <CardValue>{niiEligibleAssets}</CardValue>
        <CardSubtext>{eligibilityRate}% eligibility rate</CardSubtext>
      </ChartCard>

      <ChartCard>
        <CardHeader>Total Assets Reviewed</CardHeader>
        <CardValue>{assetsReviewed}</CardValue>
        <CardSubtext>
          {assetsReviewed} of {completionDenominator} screened ({completionRate}
          %)
        </CardSubtext>
      </ChartCard>

      <PermissionsGate
        requires={[
          PERMISSION_GECKO_INTERNAL_MEMBER,
          PERMISSION_LIBRA_EXECUTIVE,
        ]}>
        <RoiColumn>
          <ChartCard>
            <CardHeaderRow>
              <div>
                <CardHeader>Projected NII campaign ROI (AED)</CardHeader>
                <TitleValue style={{ marginTop: 2 }}>
                  {formatAed(byCategory.total)}
                </TitleValue>
              </div>
              <Button
                type='text'
                size='small'
                icon={<SettingOutlined />}
                loading={siteEntriesLoading}
                onClick={() => {
                  if (siteFilterSiteIds.length === 0) {
                    message.info('Add tickets with a site tag first.')
                    return
                  }
                  setAssumptionsOpen(true)
                }}
                aria-label='Edit site TAR and ROI'
              />
            </CardHeaderRow>

            <TrackerStrip
              $empty={byCategory.total === 0}
              role='img'
              aria-label='ROI by category'>
              {ROI_ORDER.map((k) => {
                const v = byCategory[k]
                const total = byCategory.total
                const categorySharePercent =
                  total > 0 ? Math.max(0, Math.min(100, (v / total) * 100)) : 0
                const shareLabel =
                  total > 0 ? `${categorySharePercent.toFixed(1)}%` : '—'
                const segmentWidthPercent =
                  total > 0 ? categorySharePercent : 100 / ROI_ORDER.length
                return (
                  <Tooltip
                    key={k}
                    title={
                      <TrackerTooltip>
                        <strong>{barTooltips[k].title}</strong>
                        <TrackerAmount>{formatAedFull(v)}</TrackerAmount>
                        {barTooltips[k].lines.map((line, i) => (
                          <TrackerCalcLine key={i}>{line}</TrackerCalcLine>
                        ))}
                        <TrackerMuted>Share: {shareLabel}</TrackerMuted>
                      </TrackerTooltip>
                    }>
                    <TrackerBarSegment
                      $distributeEvenly={total === 0}
                      $widthPercent={segmentWidthPercent}>
                      <TrackerSegment $color={ROI_CATEGORY_COLORS[k]} />
                    </TrackerBarSegment>
                  </Tooltip>
                )
              })}
            </TrackerStrip>

            <CategoryGrid>
              {ROI_ORDER.map((k) => (
                <CategoryCell key={k}>
                  <CategoryDot $c={ROI_CATEGORY_COLORS[k]} />
                  <CategoryName>{ROI_CATEGORY_LABELS[k]}</CategoryName>
                  <CategoryVal>{formatAed(byCategory[k])}</CategoryVal>
                </CategoryCell>
              ))}
            </CategoryGrid>
          </ChartCard>

          <RoiAssumptionsSettingsModal
            open={assumptionsOpen}
            onClose={() => setAssumptionsOpen(false)}
            group={group}
            siteOptions={siteOptions}
            selectedSiteId={_selectedSiteId}
            onSaved={onRefreshGroup}
          />
        </RoiColumn>
      </PermissionsGate>
    </ChartsGrid>
  )
}

/** Same as pre–ROI-gate layout: two equal stats + wider ROI track. */
const ChartsGrid = styled.div<{ $threeCol: boolean }>`
  display: grid;
  width: 100%;
  gap: 1rem;
  align-items: stretch;
  grid-template-columns: ${({ $threeCol }) =>
    $threeCol ? '1fr 1fr minmax(20rem, 2fr)' : '1fr 1fr'};

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`

const RoiColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`

const ChartCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colors.gray[800]};
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  min-width: 0;
`

const CardHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`

const CardHeader = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const TitleValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.forecast.activeText};
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
`

const CardValue = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: ${({ theme }) => theme.forecast.activeText};
  line-height: 1;
  margin: 0.25rem 0;
`

const CardSubtext = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  opacity: 0.8;
`

const TrackerStrip = styled.div<{ $empty: boolean }>`
  display: flex;
  width: 100%;
  height: 20px;
  border-radius: 4px;
  overflow: hidden;
  background: ${({ theme, $empty }) =>
    $empty ? theme.forecast.background2 : 'transparent'};
  margin-top: 0.5rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  cursor: default;
`

const TrackerBarSegment = styled.span<{
  $widthPercent: number
  $distributeEvenly: boolean
}>`
  display: flex;
  min-width: 4px;
  height: 100%;
  width: ${({ $widthPercent }) => `${$widthPercent}%`};
  flex: ${({ $distributeEvenly }) => ($distributeEvenly ? 1 : 'none')};
`

const TrackerSegment = styled.div<{ $color: string }>`
  width: 100%;
  height: 100%;
  min-height: 100%;
  min-width: 0;
  background: ${({ $color }) => $color};
  box-sizing: border-box;
  border-right: 1px solid rgba(0, 0, 0, 0.2);

  &:last-child {
    border-right: none;
  }
`

const TrackerTooltip = styled.div`
  font-size: 0.8rem;
  line-height: 1.4;
  max-width: 24rem;
`

const TrackerAmount = styled.div`
  font-weight: 600;
  margin: 0.2rem 0 0.35rem;
  font-variant-numeric: tabular-nums;
`

const TrackerCalcLine = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.72rem;
  line-height: 1.35;
  margin-top: 0.2rem;
`

const TrackerMuted = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
  margin-top: 0.4rem;
`

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.4rem 0.75rem;
  margin-top: 0.35rem;

  @media (min-width: 900px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`

const CategoryCell = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  column-gap: 0.4rem;
  row-gap: 0.1rem;
  font-size: 0.7rem;
  min-width: 0;
`

const CategoryDot = styled.span<{ $c: string }>`
  grid-row: 1 / span 2;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: ${({ $c }) => $c};
  margin-top: 2px;
`

const CategoryName = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CategoryVal = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-variant-numeric: tabular-nums;
  font-size: 0.75rem;
`
